import React from "react";
import PropTypes from "prop-types";
import { mediaProfileUrl, mediaOutletUrl } from "views_shared/app/javascript/utils/urls";
import MessageButton from "../../MessageButton";
import LocationMarkerIcon from "../../profiles/LocationMarkerIcon";
import SavedGroupsBookmark from "../../source_directory/SavedGroupsBookmark";
import SourceAvatar from "../../source_directory/SourceAvatar";
import HighlightAttribute, { attributeMatched } from "../../source_directory/HighlightAttribute";
import canMessageMediaProfile, { viewerIsMediaProfile } from "./messageGating";
import mediaListActionColumnWidth from "./listActionColumn";

function MediaProfileCardList({ hit }) {
  const canMessage = canMessageMediaProfile(hit.id);
  const publications = hit.publications || [];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "12px 16px",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
      }}
    >
      {/* Name / photo */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flex: "0 0 200px", minWidth: 0 }}>
        <SourceAvatar imageUrl={hit.profile_image_url} name={hit.name} initialsText={hit.initials} size={44} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>
            <a href={mediaProfileUrl({ slug: hit.slug, migrate_id: hit.migrate_id })} style={{ color: "#111827" }}>
              <HighlightAttribute hit={hit} attribute="name" fallback={hit.name} />
            </a>
          </div>
          {/* title is not searchable; title_at_workplace ("<title> at <workplace>") is, so show it
              highlighted when it matched, else the plain job title. */}
          {attributeMatched(hit, "title_at_workplace") ? (
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              <HighlightAttribute hit={hit} attribute="title_at_workplace" />
            </div>
          ) : (
            hit.title && <div style={{ fontSize: 12, color: "#6b7280" }}>{hit.title}</div>
          )}
        </div>
      </div>

      {/* Location */}
      <div style={{ flex: "0 0 130px", fontSize: 13, color: "#6b7280", display: "flex", alignItems: "center", gap: 4 }}>
        {hit.location_string && (
          <>
            <LocationMarkerIcon size={12} />
            {hit.location_string}
          </>
        )}
      </div>

      {/* Writes for */}
      <div style={{ flex: 1, fontSize: 13, color: "#212528" }}>
        {publications.map((publication, index) => (
          <React.Fragment key={publication.slug || publication.name}>
            {index > 0 && ", "}
            {publication.slug ? (
              <a
                href={mediaOutletUrl({ slug: publication.slug, migrate_id: publication.migrate_id })}
                style={{ color: "#212528", textDecoration: "underline" }}
              >
                <HighlightAttribute hit={hit} attribute={["publications", index, "name"]} fallback={publication.name} />
              </a>
            ) : (
              <HighlightAttribute hit={hit} attribute={["publications", index, "name"]} fallback={publication.name} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Actions — fixed-width column kept in sync with the list header */}
      <div
        style={{
          flex: `0 0 ${mediaListActionColumnWidth(viewerIsMediaProfile())}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 12,
        }}
      >
        {canMessage && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <MessageButton
              profileId={hit.id}
              buttonLabel="Start a Conversation"
              extraButtonClasses="mb-0 w-100"
              openInboxThread
            />
          </div>
        )}
        <SavedGroupsBookmark
          sourceableType="Id::Profile::MediaProfile"
          sourceableId={hit.id}
          sourceMeta={{ name: hit.name, imageUrl: hit.profile_image_url, slug: hit.slug }}
        />
      </div>
    </div>
  );
}

MediaProfileCardList.propTypes = {
  hit: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    slug: PropTypes.string.isRequired,
    name: PropTypes.string,
    profile_image_url: PropTypes.string,
    initials: PropTypes.string,
    title: PropTypes.string,
    title_at_workplace: PropTypes.string,
    location_string: PropTypes.string,
    publications: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, slug: PropTypes.string })),
  }).isRequired,
};

export default MediaProfileCardList;
